import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Anchor, Loader2 } from "lucide-react";
import { logActivity } from "@/services/gpsdrive-activity-log.service";
import {
  ANCHOR_NAME_PREFIX,
  useVehicleAnchor,
  useCreateGeofence,
  useCreateGeofenceAlertRules,
  useUpdateGeofence,
  useUpdateAlertRule,
  useDeleteAlertRule,
  useDeleteGeofence,
} from "@/hooks/queries";
import type { ActionKind } from "@/services/alert-rules.service";
import { parseWkt } from "@/lib/wkt";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LicensePlate } from "@/components/license-plate";

// Âncora (pedido 2026-09-04) — ver comentário completo em
// hooks/queries/index.ts#ANCHOR_NAME_PREFIX. Este diálogo é a ÚNICA UI da
// feature, reaproveitado nos dois pontos de entrada pedidos pelo usuário: o
// ícone ao lado de Bloquear/Desbloquear (routes/index.tsx e mobile-app.tsx,
// que já têm o Vehicle inteiro em mãos) e o menu de configurar na aba
// VEÍCULOS de Editar Usuário (routes/users.tsx, que só tem um
// TraccarDeviceRef sem posição — busca a posição real à parte via
// GET /vehicles/:id/profile antes de abrir este diálogo). Por isso o prop é
// um formato mínimo (não o `Vehicle` inteiro do mapa) — qualquer objeto com
// esses campos serve, incluindo um `Vehicle` de verdade.
export interface VehicleAnchorTarget {
  id: string | number;
  name: string;
  plate: string;
  lat: number;
  lng: number;
  hasPosition?: boolean;
  // Pedido 2026-09-04: sem comunicação com o rastreador agora, a posição
  // atual não é confiável pra virar centro de uma âncora nova — mesmo
  // motivo de bloquear/desbloquear recusarem quando offline (ver
  // routes/index.tsx#sendBlockCommand).
  offline?: boolean;
}

type RadiusUnit = "m" | "km";

function radiusToMeters(value: string, unit: RadiusUnit): number | null {
  const n = Number(value.replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return null;
  return unit === "km" ? n * 1000 : n;
}

function formatRadius(meters: number): string {
  return meters >= 1000 ? `${(meters / 1000).toLocaleString("pt-BR")} km` : `${Math.round(meters)} m`;
}

export function VehicleAnchorDialog({
  vehicle,
  open,
  onClose,
}: {
  vehicle: VehicleAnchorTarget;
  open: boolean;
  onClose: () => void;
}) {
  const deviceId = Number(vehicle.id);
  const { anchor, isFetched } = useVehicleAnchor(deviceId);

  const createGeofence = useCreateGeofence();
  const createRules = useCreateGeofenceAlertRules();
  const updateGeofence = useUpdateGeofence();
  const updateRule = useUpdateAlertRule(deviceId);
  const deleteRule = useDeleteAlertRule(deviceId);
  const deleteGeofence = useDeleteGeofence();

  const [radiusValue, setRadiusValue] = useState("500");
  const [radiusUnit, setRadiusUnit] = useState<RadiusUnit>("m");
  const [actionKind, setActionKind] = useState<ActionKind>("notify");
  const [busy, setBusy] = useState(false);

  // Ao abrir com uma âncora já ativa, o formulário nasce preenchido com o
  // raio/ação atuais (senão "Salvar alterações" reescreveria por engano com
  // os defaults de uma âncora nova).
  useEffect(() => {
    if (!open || !anchor) return;
    setRadiusValue(String(Math.round(anchor.radiusMeters)));
    setRadiusUnit("m");
    setActionKind(anchor.actionKind);
  }, [open, anchor]);

  // Posição real do veículo em coordenadas 0,0 (sem fix de GPS ainda) nunca
  // deve virar centro de âncora — mesmo critério de hasRealCoords em
  // vehicles.service.ts.
  const hasValidPosition = vehicle.hasPosition !== false && (vehicle.lat !== 0 || vehicle.lng !== 0);

  const anchorName = `${ANCHOR_NAME_PREFIX}${vehicle.name}`;

  const handleActivate = async () => {
    const radiusMeters = radiusToMeters(radiusValue, radiusUnit);
    if (!radiusMeters) {
      toast.error("Informe um raio válido");
      return;
    }
    // Pedido 2026-09-04: sem comunicação com o rastreador agora, a posição
    // atual não é confiável pra virar centro — mesmo critério do
    // Bloquear/Desbloquear (ver routes/index.tsx#sendBlockCommand).
    if (vehicle.offline) {
      toast.error("Não foi possível ativar a âncora", {
        description: `${vehicle.name} — veículo offline (sem comunicação com o rastreador agora).`,
      });
      logActivity({
        type: "ERROR",
        message: `Ativação de âncora recusada — ${vehicle.name} está offline`,
        metadata: { vehicleId: deviceId, reason: "device_offline" },
      });
      return;
    }
    setBusy(true);
    try {
      const geofence = await createGeofence.mutateAsync({
        name: anchorName,
        area: `CIRCLE (${vehicle.lng} ${vehicle.lat}, ${radiusMeters})`,
      });
      await createRules.mutateAsync({
        geofenceId: geofence.id,
        deviceIds: [deviceId],
        name: anchorName,
        triggerWhen: "exit",
        actionKind,
        channelApp: true,
        channelEmail: false,
        dedupeMinutes: 5,
      });
      toast.success("Âncora ativada", {
        description: `${vehicle.name} — raio de ${formatRadius(radiusMeters)}.`,
      });
      logActivity({
        type: "VEHICLE_COMMAND",
        message: `Ativou a âncora de ${vehicle.name} (${vehicle.plate}) — raio ${formatRadius(radiusMeters)}, ${actionKind === "block" ? "alertar e bloquear" : "somente alertar"}`,
        metadata: { vehicleId: deviceId, radiusMeters, actionKind, command: "anchor_activate" },
      });
      onClose();
    } catch (err) {
      // Sem logActivity de ERROR aqui de propósito — createGeofence/
      // createRules já passam por gpsdrive-client.ts, que já loga
      // qualquer chamada mutante que falhe (ver comentário lá); duplicar
      // aqui só repetiria a mesma entrada duas vezes no histórico.
      toast.error("Falha ao ativar a âncora", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async (reanchorHere: boolean) => {
    if (!anchor) return;
    const radiusMeters = radiusToMeters(radiusValue, radiusUnit);
    if (!radiusMeters) {
      toast.error("Informe um raio válido");
      return;
    }
    if (reanchorHere && vehicle.offline) {
      toast.error("Não foi possível reancorar", {
        description: `${vehicle.name} — veículo offline (sem comunicação com o rastreador agora).`,
      });
      logActivity({
        type: "ERROR",
        message: `Reancorar recusado — ${vehicle.name} está offline`,
        metadata: { vehicleId: deviceId, reason: "device_offline" },
      });
      return;
    }
    setBusy(true);
    try {
      // "Reancorar aqui" recentraliza no ponto atual do veículo; caso
      // contrário, mantém o centro já salvo na geocerca e só troca o raio.
      let centerLat = vehicle.lat;
      let centerLng = vehicle.lng;
      if (!reanchorHere) {
        const parsed = parseWkt(anchor.geofence.area);
        if (parsed?.type === "circle") {
          centerLat = parsed.data.lat;
          centerLng = parsed.data.lng;
        }
      }
      await updateGeofence.mutateAsync({
        id: anchor.geofence.id,
        data: { area: `CIRCLE (${centerLng} ${centerLat}, ${radiusMeters})` },
      });
      if (actionKind !== anchor.actionKind) {
        await updateRule.mutateAsync({ id: anchor.rule.id, patch: { actionKind } });
      }
      toast.success(reanchorHere ? "Âncora reposicionada" : "Âncora atualizada", {
        description: `${vehicle.name} — raio de ${formatRadius(radiusMeters)}.`,
      });
      logActivity({
        type: "VEHICLE_COMMAND",
        message: `${reanchorHere ? "Reancorou" : "Atualizou a âncora de"} ${vehicle.name} (${vehicle.plate}) — raio ${formatRadius(radiusMeters)}, ${actionKind === "block" ? "alertar e bloquear" : "somente alertar"}`,
        metadata: { vehicleId: deviceId, radiusMeters, actionKind, command: reanchorHere ? "anchor_reanchor" : "anchor_update" },
      });
      onClose();
    } catch (err) {
      // Sem logActivity de ERROR aqui — mesmo motivo de handleActivate
      // acima (gpsdrive-client.ts já loga a falha HTTP real).
      toast.error("Falha ao atualizar a âncora", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async () => {
    if (!anchor) return;
    if (!window.confirm(`Desativar a âncora de ${vehicle.name}? O veículo não será desbloqueado automaticamente.`)) {
      return;
    }
    setBusy(true);
    try {
      await deleteRule.mutateAsync(anchor.rule.id);
      await deleteGeofence.mutateAsync(anchor.geofence.id);
      toast.success("Âncora desativada", { description: vehicle.name });
      logActivity({
        type: "VEHICLE_COMMAND",
        message: `Desativou a âncora de ${vehicle.name} (${vehicle.plate})`,
        metadata: { vehicleId: deviceId, command: "anchor_deactivate" },
      });
      onClose();
    } catch (err) {
      toast.error("Falha ao desativar a âncora", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Anchor className="h-4 w-4" /> Âncora
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">{vehicle.name} <LicensePlate plate={vehicle.plate} size="xs" /></DialogDescription>
        </DialogHeader>

        {!isFetched ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
          </div>
        ) : !hasValidPosition ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Este veículo ainda não tem uma posição de GPS válida — a âncora precisa de uma
            posição real para definir o centro do raio.
          </p>
        ) : (
          <div className="space-y-4">
            {anchor && (
              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                Âncora ativa — raio atual de <strong>{formatRadius(anchor.radiusMeters)}</strong>,{" "}
                {anchor.actionKind === "block" ? "alerta e bloqueio automático" : "somente alerta"}.
              </div>
            )}

            <div className="grid grid-cols-[1fr_auto] gap-2">
              <div>
                <Label htmlFor="anchor-radius">Raio permitido</Label>
                <Input
                  id="anchor-radius"
                  inputMode="decimal"
                  value={radiusValue}
                  onChange={(e) => setRadiusValue(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Unidade</Label>
                <Select value={radiusUnit} onValueChange={(v) => setRadiusUnit(v as RadiusUnit)}>
                  <SelectTrigger className="mt-1 w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="m">metros</SelectItem>
                    <SelectItem value="km">km</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Ao sair do raio</Label>
              <RadioGroup
                value={actionKind}
                onValueChange={(v) => setActionKind(v as ActionKind)}
                className="mt-2 space-y-2"
              >
                <label className="flex items-center gap-2 text-sm">
                  <RadioGroupItem value="notify" /> Somente alertar
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <RadioGroupItem value="block" /> Alertar e bloquear automaticamente
                </label>
              </RadioGroup>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {anchor ? (
            <>
              <div className="flex w-full gap-2">
                <Button className="flex-1" disabled={busy || !hasValidPosition} onClick={() => handleUpdate(false)}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar alterações"}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={busy || !hasValidPosition || vehicle.offline}
                  title={vehicle.offline ? "Veículo offline" : undefined}
                  onClick={() => handleUpdate(true)}
                >
                  Reancorar aqui
                </Button>
              </div>
              <Button variant="destructive" className="w-full" disabled={busy} onClick={handleDeactivate}>
                Desativar Âncora
              </Button>
            </>
          ) : (
            <Button
              className="w-full"
              disabled={busy || !hasValidPosition || vehicle.offline}
              title={vehicle.offline ? "Veículo offline" : undefined}
              onClick={handleActivate}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ativar Âncora aqui"}
            </Button>
          )}
          {vehicle.offline && (
            <p className="text-center text-xs text-destructive">
              Veículo offline — sem comunicação com o rastreador agora.
            </p>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
